import { useCallback, useState } from 'react';

import { type ConversationRecipient } from '@apps/web/components/shared/messages/conversation_recipient_picker';
import { inboxConversationPhoneFilter } from '@apps/web/lib/conversation_deep_link';

export const LIST_LAYOUT_TRANSITION = {
    type: 'spring',
    visualDuration: 0.28,
    bounce: 0,
} as const;

export type ConversationsInboxDeepLink = {
    readonly conversationId?: string;
    readonly messageId?: string;
    readonly caregiverId?: string;
    readonly clientId?: string;
    readonly contactPhone?: string;
};

export type ContactPhoneDeepLinkAction = 'wait' | 'open' | 'compose';

/**
 * Inbox profile phones promise a conversation even when none exists yet.
 * Opening the newest unrelated thread is the failure this blocks.
 */
export function contactPhoneDeepLinkAction({
    shouldResolve,
    isLoading,
    conversationId,
}: {
    readonly shouldResolve: boolean;
    readonly isLoading: boolean;
    readonly conversationId: string | undefined;
}): ContactPhoneDeepLinkAction {
    if (!shouldResolve || isLoading) {
        return 'wait';
    }
    return conversationId ? 'open' : 'compose';
}

/**
 * A person deep link with no thread still needs a sendable draft. Caregiver
 * metadata stays on the recipient so send uses caregiver_id; a normalized
 * phone is the textable address when we have one.
 */
export function noHistoryConversationRecipient({
    caregiverId,
    caregiverName,
    phone,
}: {
    readonly caregiverId?: string;
    readonly caregiverName?: string | null;
    readonly phone?: string | null;
}): ConversationRecipient | null {
    const normalizedPhone = inboxConversationPhoneFilter(phone);
    if (caregiverId && caregiverName) {
        return {
            kind: 'caregiver',
            caregiver: {
                id: caregiverId,
                name: caregiverName,
                ...(normalizedPhone ? { phoneNumber: normalizedPhone } : {}),
            },
        };
    }
    if (!normalizedPhone) {
        return null;
    }
    return { kind: 'phoneNumber', phoneNumber: normalizedPhone };
}

/**
 * Client-only "New conversation" draft. Independent of selectedId: clearing
 * the open thread must not look like "nothing selected" to desktop auto-open,
 * and the draft pane must render without a conversation id.
 */
export function useConversationsInboxDraft(options: {
    readonly clearSelectedConversation: () => void;
}) {
    const { clearSelectedConversation } = options;
    const [isComposingNew, setIsComposingNew] = useState(false);
    // Whether the draft, rather than a conversation, is what the detail pane
    // is showing. A draft outlives reading another thread -- its row stays in
    // the list -- so "a draft exists" and "the draft is what you are looking
    // at" are two different questions.
    const [isDraftSelected, setIsDraftSelected] = useState(false);
    const [draftRecipient, setDraftRecipient] =
        useState<ConversationRecipient | null>(null);

    const startNewConversation = useCallback(
        (recipient?: ConversationRecipient | null) => {
            setIsComposingNew(true);
            setIsDraftSelected(true);
            setDraftRecipient(recipient ?? null);
            clearSelectedConversation();
        },
        [clearSelectedConversation],
    );

    const cancelNewConversation = useCallback(() => {
        setIsComposingNew(false);
        setIsDraftSelected(false);
        setDraftRecipient(null);
    }, []);

    /** Clicking the draft row: hand the pane back to the unsent message. */
    const selectDraft = useCallback(() => {
        setIsDraftSelected(true);
        clearSelectedConversation();
    }, [clearSelectedConversation]);

    /** Opening a conversation: the pane moves on, the draft row stays. */
    const deselectDraft = useCallback(() => {
        setIsDraftSelected(false);
    }, []);

    return {
        isComposingNew,
        isDraftSelected,
        draftRecipient,
        startNewConversation,
        cancelNewConversation,
        selectDraft,
        deselectDraft,
    };
}

/** Desktop auto-open must not steal an existing row while a draft is open. */
export function shouldDesktopAutoOpenConversation({
    isMobile,
    selectedId,
    isComposingNew,
    deepLinkConversationId,
    shouldResolveCaregiver,
    shouldResolveClient,
    shouldResolveContactPhone,
    isLoading,
    hasRows,
}: {
    readonly isMobile: boolean;
    readonly selectedId: string | null;
    readonly isComposingNew: boolean;
    readonly deepLinkConversationId: string | undefined;
    readonly shouldResolveCaregiver: boolean;
    readonly shouldResolveClient: boolean;
    readonly shouldResolveContactPhone: boolean;
    readonly isLoading: boolean;
    readonly hasRows: boolean;
}): boolean {
    if (isMobile || selectedId !== null || isComposingNew) {
        return false;
    }
    if (
        deepLinkConversationId ||
        shouldResolveCaregiver ||
        shouldResolveClient ||
        shouldResolveContactPhone
    ) {
        return false;
    }
    if (isLoading || !hasRows) {
        return false;
    }
    return true;
}
